import * as ImagePicker from "expo-image-picker";
import { FC, useState } from "react";

import { MediaPicker, VStack } from "@/components/ui";
import { Question, QuestionDraft, Rule } from "@/types/resources";
import { QuestionSuggestion } from "@/types/responses";
import {
  RulesEditor,
  conditionSourceQuestions,
  hasApplicableRules,
} from "../rules";
import AdvancedSettings from "./AdvancedSettings";
import QuestionField from "./QuestionField";
import QuestionTextInput from "./QuestionTextInput";

interface OpenEndedQuestionEditorProps {
  allQuestions?: Question[];
  currentQuestionOrder?: number;
  disabled?: boolean;
  onChange: (question: QuestionDraft<"open_ended">) => void;
  question: QuestionDraft<"open_ended">;
  questionOrder?: number;
}

const MAX_MEDIA = 10;

const OpenEndedQuestionEditor: FC<OpenEndedQuestionEditorProps> = ({
  allQuestions = [],
  currentQuestionOrder,
  disabled = false,
  onChange,
  question,
  questionOrder,
}) => {
  const [isSuggesting, setIsSuggesting] = useState(false);
  const isDisabled = disabled || isSuggesting;

  const handleQuestionTextChange = (text: string) => {
    onChange({ ...question, text });
  };

  const handleSuggest = (suggestion: QuestionSuggestion) => {
    onChange({ ...question, text: suggestion.text });
  };

  const handleImagesChange = (
    newImages: (ImagePicker.ImagePickerAsset | string)[]
  ) => {
    onChange({ ...question, media: newImages });
  };

  const rules = question.rules || [];

  const previousConditionQuestions = conditionSourceQuestions(
    allQuestions,
    currentQuestionOrder
  );

  const handleRulesChange = (newRules: Rule[]) => {
    onChange({ ...question, rules: newRules });
  };

  return (
    <MediaPicker
      isDisabled={isDisabled}
      maxItems={MAX_MEDIA}
      media={question.media}
      onMediaChange={handleImagesChange}
    >
      <VStack className="gap-6">
        <VStack className="gap-3">
          <QuestionField order={questionOrder}>
            <QuestionTextInput
              disabled={disabled}
              lines={question.lines}
              onChange={handleQuestionTextChange}
              onLoadingChange={setIsSuggesting}
              onSuggest={handleSuggest}
              options={question.options}
              text={question.text || ""}
              type={question.type}
            />
            <MediaPicker.Trigger compact height={70} width={70} />
          </QuestionField>
          <MediaPicker.Thumbnails />
          <MediaPicker.Error />
        </VStack>

        {/* Skip logic */}
        {hasApplicableRules(allQuestions, currentQuestionOrder) && (
          <AdvancedSettings defaultExpanded={rules.length > 0}>
            <RulesEditor
              allQuestions={allQuestions}
              currentQuestionOrder={currentQuestionOrder}
              disabled={isDisabled}
              onRulesChange={handleRulesChange}
              previousConditionQuestions={previousConditionQuestions}
              rules={rules}
            />
          </AdvancedSettings>
        )}
      </VStack>
    </MediaPicker>
  );
};

export default OpenEndedQuestionEditor;
